import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import "tippy.js/dist/tippy.css";
import "tippy.js/themes/material.css";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  wsReadyState,
  currentCanvasIdState,
  activeScopeState,
} from "../global-state";
import { listCanvas } from "../service/ui-service";
import { useToast } from "../common/toast";
import { useWebSocket } from "../ws/websocket";
import { Alert, Typography } from "@mhome/ui";
import { Tabs as MuiTabs, Tab as MuiTab } from "@mui/material"; // Retained for scrollable variant
import InfoIcon from "@mui/icons-material/Info";
import { createGlobalStyle } from "styled-components";
import { alpha } from "@mui/material/styles";
import DashboardCanvas from "./dashboard-canvas";

const GlobalStyle = createGlobalStyle`
`;

const DashboardView = forwardRef(
  (
    {
      drag = false,
      dragClose = () => {},
      setIsBottomNavVisible,
      isChatSidebarVisible = false,
      onTempStackChange = () => {},
      onMenuAction = () => {},
    },
    ref
  ) => {
    const wsReady = useRecoilValue(wsReadyState);
    const [currentCanvasId, setCurrentCanvasId] =
      useRecoilState(currentCanvasIdState);
    const { errorToast } = useToast();
    const tabsContainerRef = useRef(null);
    const location = useLocation();

    const curCanvasRef = useRef("");
    const history = useHistory();
    const activeScope = useRecoilValue(activeScopeState);
    const { sendAndWaitWs } = useWebSocket();
    const [canvasList, setCanvasList] = useState([]);
    const [editCardLayoutMode, setEditCardLayoutMode] = useState(false);

    const getCurrentLayoutSize = useCallback(() => {
      const width = window.innerWidth;
      const effectiveWidth = isChatSidebarVisible ? width - 400 : width;
      if (effectiveWidth <= 768) return "sm";
      if (effectiveWidth <= 1200) return "md";
      return "lg";
    }, [isChatSidebarVisible]);

    const getLayoutSizeName = (layoutSize) => {
      switch (layoutSize) {
        case "sm":
          return "small";
        case "md":
          return "medium";
        case "lg":
          return "large";
        default:
          return "current";
      }
    };

    useEffect(() => {
      if (drag || editCardLayoutMode) {
        setIsBottomNavVisible(false);
      } else {
        setIsBottomNavVisible(true);
      }
    }, [drag, editCardLayoutMode, setIsBottomNavVisible]);

    // Initialize current canvas from URL or global state
    useEffect(() => {
      const searchParams = new URLSearchParams(location.search);
      const canvasIdFromUrl = searchParams.get("canvas");

      if (canvasIdFromUrl) {
        // URL takes priority - update both ref and global state
        curCanvasRef.current = canvasIdFromUrl;
        setCurrentCanvasId(canvasIdFromUrl);
      } else if (currentCanvasId && !curCanvasRef.current) {
        // Use global state if no URL param and no current canvas set
        curCanvasRef.current = currentCanvasId;
        // Update URL to reflect current canvas
        if (history.location.pathname === "/dashboard") {
          history.replace(`/dashboard?canvas=${currentCanvasId}`);
        }
      }
    }, [location.search, currentCanvasId, setCurrentCanvasId]);

    // Load canvas list
    useEffect(() => {
      const loadCanvasList = async () => {
        try {
          const resp = await listCanvas(sendAndWaitWs);
          if (resp.canvas) {
            setCanvasList(resp.canvas);

            // Validate current canvas exists in the list
            const canvasExists =
              curCanvasRef.current &&
              resp.canvas.some((canvas) => canvas.id === curCanvasRef.current);

            if (!canvasExists && resp.canvas.length > 0) {
              // Current canvas doesn't exist or not set, use first canvas
              const firstCanvas = resp.canvas[0];
              curCanvasRef.current = firstCanvas.id;
              setCurrentCanvasId(firstCanvas.id);

              // Update URL if we're on dashboard page
              if (history.location.pathname === "/dashboard") {
                history.replace(`/dashboard?canvas=${firstCanvas.id}`);
              }
            } else if (canvasExists) {
              // Canvas exists, ensure global state is synced
              setCurrentCanvasId(curCanvasRef.current);
            }
          } else {
            errorToast("Get Canvas Error");
          }
        } catch (error) {
          errorToast("Error to load canvas");
        }
      };

      if (wsReady) {
        loadCanvasList();
      }
    }, [wsReady, activeScope]);

    const handleTabClick = (canvasId) => {
      // Reset drag state when switching tabs
      if (drag) {
        dragClose(); // This should reset drag to false
      }

      // Reset edit card layout mode when switching tabs
      if (editCardLayoutMode) {
        setEditCardLayoutMode(false);
      }

      // Update current canvas ref and global state
      curCanvasRef.current = canvasId;
      setCurrentCanvasId(canvasId);

      // Update URL
      if (history.location.pathname === "/dashboard") {
        history.replace(`/dashboard?canvas=${canvasId}`);
      }
    };

    const canvasRef = useRef();

    const handleOpenOptionDialog = () => {
      // Trigger the canvas component to open its AddOptionDialog
      if (canvasRef.current?.openOptionDialog) {
        canvasRef.current.openOptionDialog();
      }
    };

    // Handle menu actions specific to dashboard
    const handleMenuAction = (action) => {
      switch (action) {
        case "canvas-manage":
          // Navigate to canvas management
          onMenuAction("navigate", "/dashboard/manage");
          break;
        case "toggle-drag":
          dragClose();
          break;
        case "add-card":
          handleOpenOptionDialog();
          break;
        default:
          break;
      }
    };

    // Expose methods through ref
    useImperativeHandle(ref, () => ({
      openOptionDialog: handleOpenOptionDialog,
      handleMenuAction: handleMenuAction,
    }));

    // Center active tab in view
    useEffect(() => {
      if (tabsContainerRef.current && curCanvasRef.current && !drag) {
        const activeTab = tabsContainerRef.current.querySelector(
          `[data-canvas-id="${curCanvasRef.current}"]`
        );
        if (activeTab) {
          // Find the scrollable container within the Tabs component
          const scrollContainer =
            tabsContainerRef.current.querySelector(".MuiTabs-scroller");
          if (scrollContainer) {
            const containerWidth = scrollContainer.offsetWidth;
            const tabLeft = activeTab.offsetLeft;
            const tabWidth = activeTab.offsetWidth;
            const scrollLeft = tabLeft - containerWidth / 2 + tabWidth / 2;

            scrollContainer.scrollTo({
              left: Math.max(0, scrollLeft),
              behavior: "smooth",
            });
          }
        }
      }
    }, [canvasList, curCanvasRef.current, drag]);

    return (
      <>
        <GlobalStyle />
        <div className="h-full visible relative flex flex-col overflow-hidden">
          <div
            data-tabs-container="true"
            className="relative h-12 min-h-[48px] z-10 overflow-visible shrink-0"
          >
            {/* Show info message in drag mode */}
            {drag && (
              <div className="relative top-0 left-0 right-0 z-[1] bg-transparent flex items-center gap-2 h-full">
                <div
                  className="flex-1 flex items-center justify-center h-full px-4"
                  style={{ color: "hsl(var(--foreground))" }}
                >
                  <Alert
                    severity="info"
                    variant="standard"
                    className="!items-center px-4 py-2 bg-transparent border-0 flex justify-center [&>div:first-child]:mt-0"
                    style={{ border: "none", borderWidth: 0 }}
                    icon={
                      <InfoIcon
                        className="cursor-pointer"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "hsl(var(--primary))";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "hsl(var(--primary))";
                        }}
                      />
                    }
                  >
                    <Typography variant="body2" className="font-medium m-0">
                      Layout the {getLayoutSizeName(getCurrentLayoutSize())}{" "}
                      dashboard
                    </Typography>
                  </Alert>
                </div>
              </div>
            )}

            {/* Show canvas tabs when not in drag mode */}
            {!drag && (
              <div className="relative top-0 left-0 right-0 z-[1] bg-transparent backdrop-filter-none shadow-none rounded-none flex items-center gap-2 h-full">
                <MuiTabs
                  ref={tabsContainerRef}
                  value={canvasList.findIndex(
                    (canvas) => canvas.id === curCanvasRef.current
                  )}
                  onChange={(event, newValue) => {
                    if (newValue >= 0 && newValue < canvasList.length) {
                      handleTabClick(canvasList[newValue].id);
                    }
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    flex: 1,
                    height: "100%",
                    minHeight: "48px",
                    "& .MuiTabs-flexContainer": {
                      height: "100%",
                      alignItems: "center",
                      padding: "0 16px",
                    },
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      letterSpacing: "0.01em",
                      minWidth: "fit-content",
                      minHeight: "28px",
                      height: "28px",
                      padding: "0 12px",
                      margin: "2px 6px",
                      borderRadius: 4,
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      color: "text.secondary",
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                        fontWeight: 600,
                      },
                      "&:hover": {
                        backgroundColor: alpha("hsl(var(--primary))", 0.08),
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "primary.dark",
                      },
                      "&.drag-hover": {
                        backgroundColor: alpha("hsl(var(--primary))", 0.15),
                        color: "hsl(var(--primary))",
                        fontWeight: 600,
                        transform: "translateY(-2px)",
                        boxShadow: `0 2px 8px ${alpha(
                          "hsl(var(--primary))",
                          0.25
                        )}`,
                      },
                    },
                    "& .MuiTabs-indicator": {
                      display: "none", // Hide the default indicator since we're using custom styling
                    },
                    "& .MuiTabs-scrollButtons": {
                      color: "text.secondary",
                      "&.Mui-disabled": {
                        opacity: 0.3,
                      },
                    },
                  }}
                >
                  {canvasList.map((canvas) => (
                    <MuiTab
                      key={canvas.id}
                      label={canvas.name}
                      className="canvas-tab"
                      data-canvas-id={canvas.id}
                      data-canvas-name={canvas.name}
                    />
                  ))}
                </MuiTabs>
              </div>
            )}
          </div>

          {/* Main canvas content */}
          <DashboardCanvas
            ref={canvasRef}
            canvasId={curCanvasRef.current}
            drag={drag}
            dragClose={dragClose}
            editCardLayoutMode={editCardLayoutMode}
            setEditCardLayoutMode={setEditCardLayoutMode}
            isChatSidebarVisible={isChatSidebarVisible}
            onTempStackChange={onTempStackChange}
          />
        </div>
      </>
    );
  }
);

DashboardView.displayName = "DashboardView";
export default DashboardView;
